/**
 * 🔧 EXECUTIVE WIDGET OPERATIONS API
 * ════════════════════════════════════════════════════════════════════════
 * 
 * APIs for widget operations in Executive dashboards
 * 
 * CRITICAL RULES:
 * 1. Remove: Backend recalculates ALL layouts on Save (not during draft)
 * 2. Duplicate: Backend returns ONLY new widget with its layout
 *    - Does NOT move existing widgets
 *    - Existing widgets keep their positions in preview
 * 3. All operations return full dashboard state after processing
 */

import api from '../../../shared/services/apiClient';

/**
 * Remove widgets from dashboard
 * Backend recalculates ALL layouts for remaining widgets
 * 
 * Called ONLY on Save (not during EDIT mode drafting)
 */
export async function removeWidgets(dashboardId: string | number, widgetIds: string[]) {
  const response = await api.post(
    `/dashboards/${dashboardId}/widgets/remove`,
    { widgetIds }
  );
  return response.data; // { dashboard: FullDashboard }
}

/**
 * Duplicate a widget
 * Backend generates layout ONLY for the new widget
 * 
 * DOES NOT move existing widgets during preview
 * Full recalculation happens only on final Save
 * 
 * Returns: New widget with layout (to add to draft)
 */
export async function duplicateWidget(dashboardId: string | number, widgetId: string) {
  const response = await api.post(
    `/dashboards/${dashboardId}/widgets/duplicate`,
    { widgetId }
  );
  return response.data; // { widget: ExecutiveWidget } - ONLY the new widget
}

/**
 * Save draft changes (remove + duplicate operations)
 * Backend recalculates ALL layouts for final optimized state
 * 
 * This is where full recalculation happens
 */
export async function saveDraftChanges(
  dashboardId: string | number,
  operations: {
    removedWidgetIds?: string[];
    duplicatedWidgets?: Array<{ originalId: string; newWidget: any }>;
  }
) {
  const response = await api.post(
    `/dashboards/${dashboardId}/apply-changes`,
    operations
  );
  return response.data; // { dashboard: FullDashboard } - After full recalculation
}
